import { grey } from "@mui/material/colors"
import { styled, useTheme } from "@mui/material/styles"
import { ChartsGrid } from "@mui/x-charts/ChartsGrid"
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip"
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis"
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis"
import { LinePlot } from "@mui/x-charts/LineChart"
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer"
import { useDrawingArea, useXAxis, useXScale } from "@mui/x-charts/hooks"
import { useEffect, useRef } from "react"

import { formatCurrency } from "../../../../../common/utils"
import ChartCard from "./ChartCard"

export default function SalesVolumeChart({
  candidate,
  salesVolumeChartData,
  isLoadingSalesVolumeChart,
}) {
  const theme = useTheme()
  const normalizedSimilarProductionsObject = Object.fromEntries(
    salesVolumeChartData.annual_productions_similar_agents.map(
      ({ year, value }) => [year, value]
    )
  )

  const chartData = salesVolumeChartData.annual_productions.map(
    ({ year, value }) => ({
      year,
      agentProd: value,
      similarTenureAgentProd: normalizedSimilarProductionsObject[year],
    })
  )
  const CHART_HEIGHT = 200 + OFFICE_NAME_RECT_HEIGHT

  return (
    <ChartCard
      title="Sales Volume"
      legendLabel1={candidate.candidate_name}
      legendLabel2="Agents with similar tenure"
      chartHeight={CHART_HEIGHT}
    >
      <ResponsiveChartContainer
        dataset={chartData}
        xAxis={[
          {
            id: SALES_VOLUME_DATA_KEYS.year,
            dataKey: SALES_VOLUME_DATA_KEYS.year,
            // prevent partial years like 2025.5 from showing up on x-axis
            tickLabelInterval: (value) => value === Math.floor(value),
            tickSize: 5,
            tickLabelStyle: { fontSize: 12 },
            valueFormatter: (value) => value.toString(),
          },
        ]}
        yAxis={[
          {
            tickSize: 5,
            tickLabelStyle: { fontSize: 12 },
            valueFormatter: formatCurrency,
          },
        ]}
        series={[
          {
            type: "line",
            label: candidate.candidate_name,
            dataKey: SALES_VOLUME_DATA_KEYS.agentProd,
            showMark: false,
            curve: "linear",
            area: false,
            color: "#9525D6",
            valueFormatter: formatCurrency,
          },
          {
            type: "line",
            label: "Cohort",
            dataKey: SALES_VOLUME_DATA_KEYS.similarTenureAgentProd,
            showMark: false,
            curve: "linear",
            area: false,
            color: "#D386FF",
            valueFormatter: formatCurrency,
          },
        ]}
        height={CHART_HEIGHT}
        margin={{
          top: OFFICE_NAME_RECT_HEIGHT + 10,
          right: 20,
          bottom: 20,
          left: 45,
        }}
        slotProps={{
          animation: {
            duration: 800,
            easing: "ease-in-out",
          },
        }}
        sx={{
          "& .MuiLineElement-root": {
            strokeWidth: 2,
            border: "2px solid #D386FF",
          },
          "& .MuiChartsAxis-root": {
            display: "block",
            "& .MuiChartsAxis-tick": {
              fontSize: "12px",
              fill: theme.palette.text.secondary,
            },
            "& .MuiChartsAxis-tickLabel": {
              fontSize: "12px",
              fill: theme.palette.text.secondary,
            },
            "& .MuiChartsAxis-line": {
              stroke: theme.palette.divider,
              strokeWidth: 1,
            },
          },
        }}
      >
        <LinePlot />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsGrid horizontal vertical />
        <ChartsTooltip />
        <OfficeHistoryBar
          officeMovements={salesVolumeChartData.office_movements}
        />
      </ResponsiveChartContainer>
    </ChartCard>
  )
}

function OfficeHistoryBar({ officeMovements }) {
  const { left, top, width } = useDrawingArea()
  const xAxis = useXAxis()
  // office movements can include movements that are outside of chart time limit
  let filteredOfficeMovements = officeMovements.slice()
  const chartYears = new Set(xAxis.data)
  filteredOfficeMovements = filteredOfficeMovements.filter((movement, i) => {
    // if the office life span (until next movement) is in chart years
    const movementYear = getMovementYear(movement)
    const nextOfficeMovement = filteredOfficeMovements[i + 1]
    if (nextOfficeMovement) {
      const nextMovementYear = getMovementYear(nextOfficeMovement)
      // can't use Set.intersection() method as fairly new
      for (let year = movementYear; year <= nextMovementYear; year++) {
        if (chartYears.has(year)) {
          return true
        }
      }
      return false
    }
    // always show last office
    return true
  })
  const xAxisScale = useXScale()
  let movingRectWidth = 0

  function getMovementYear(movement) {
    return new Date(movement.date).getFullYear()
  }
  return filteredOfficeMovements.map((movement, index) => {
    const leftOffset = left + movingRectWidth
    let rectWidth
    let nextOfficeMovement = filteredOfficeMovements[index + 1]
    if (nextOfficeMovement) {
      rectWidth =
        Math.max(
          xAxisScale(new Date(nextOfficeMovement.date).getFullYear()).toFixed(
            0
          ),
          left
        ) -
        left -
        movingRectWidth
    } else {
      rectWidth = width - movingRectWidth
    }
    movingRectWidth += rectWidth
    return (
      <g>
        <OfficeNameRect
          x={leftOffset}
          y={top - OFFICE_NAME_RECT_HEIGHT}
          width={rectWidth}
          index={index}
        >
          <title>{movement.office_name}</title>
        </OfficeNameRect>
        <TrimLongOfficeNameText
          rectWidth={rectWidth}
          leftOffset={leftOffset}
          officeMovement={movement}
        />
      </g>
    )
  })
}

function TrimLongOfficeNameText({ rectWidth, leftOffset, officeMovement }) {
  const textRef = useRef()
  const { top } = useDrawingArea()
  const topMargin = top - OFFICE_NAME_RECT_HEIGHT

  // see: https://stackoverflow.com/a/24120935/1433559
  function placeTextWithEllipsis() {
    const textObj = textRef.current
    const textString = officeMovement.office_name
    textObj.textContent = textString

    if (textObj.getSubStringLength(0, textString.length) >= rectWidth) {
      for (var x = textString.length - 3; x > 0; x -= 3) {
        if (textObj.getSubStringLength(0, x) <= rectWidth) {
          textObj.textContent = textString.substring(0, x) + "..."
          return
        }
      }
      textObj.textContent = "..."
    }
  }

  useEffect(
    () => {
      if (!textRef.current) {
        return
      }
      placeTextWithEllipsis()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [textRef]
  )
  return (
    <OfficeNameText
      x={rectWidth / 2 + leftOffset}
      y={topMargin + OFFICE_NAME_RECT_HEIGHT / 2}
      width={rectWidth}
      ref={textRef}
    >
      {officeMovement.office_name}
    </OfficeNameText>
  )
}

const SALES_VOLUME_DATA_KEYS = {
  year: "year",
  agentProd: "agentProd",
  similarTenureAgentProd: "similarTenureAgentProd",
}

const OFFICE_NAME_RECT_HEIGHT = 40

const OfficeNameRect = styled("rect", {
  shouldForwardProp: (prop) => prop !== "index",
})(({ theme, index }) => ({
  fill: index % 2 === 0 ? grey[300] : grey[400],
  height: OFFICE_NAME_RECT_HEIGHT,
}))

const OfficeNameText = styled("text")(({ theme }) => ({
  fill: theme.palette.text.primary,
  fontSize: "12px",
  fontWeight: "bold",
  dominantBaseline: "middle",
  textAnchor: "middle",
}))
